import { assertOriginalRuleConfiguration } from '../utils/originalRuleConfiguration';
/**
 * RuleEvaluator - Orchestrates all rule processing for mentions
 *
 * This is the main entry point for rule evaluation. It coordinates the
 * processing of zero-tolerance, accountability, and mention rules in the
 * correct order with proper priority.
 */

import { isSchoolRulePeriodError } from '../utils/dateHelpers';

import {
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { getOffenceField } from '../utils/fieldNormalizer';
import { typeNormalizer } from '../utils/TypeNormalizer';
import { ZeroToleranceRuleService } from './ZeroToleranceRuleService';
import { AccountabilityRuleService } from './AccountabilityRuleService';
import { MentionRuleService } from './MentionRuleService';
import { ThresholdRuleService } from './ThresholdRuleService';
import { EscalationRuleService } from './EscalationRuleService';
import { ScoreAggregationRuleService } from './ScoreAggregationRuleService';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { studentDataFetcher } from './StudentDataFetcher';
import { contractCache } from '../core/ContractCache';
import { processingGuard } from '../utils/ProcessingGuard';

export class RuleEvaluator {
  constructor(dependencies) {
    // Store service dependencies
    this.mentionService = dependencies.mentionService;
    this.ruleService = dependencies.ruleService;
    this.consequenceService = dependencies.consequenceService;
    this.typeNormalizer = typeNormalizer; // Centralized type normalization

    // Which domain this evaluator serves. Defaults to 'discipline' so the
    // existing discipline wiring is unchanged; the factory passes the real
    // domain id for every other domain so student-data slices the right events.
    this.domainId = dependencies.domainId || 'discipline';

    // Initialize rule services
    const ruleServiceDeps = {
      ruleService: this.ruleService,
      mentionService: this.mentionService,
      consequenceService: this.consequenceService,
      contractService: dependencies.contractService || null,
      typeNormalizer: this.typeNormalizer // Pass to all rule services
    };

    this.zeroToleranceService = new ZeroToleranceRuleService(ruleServiceDeps);
    this.accountabilityService = new AccountabilityRuleService(ruleServiceDeps);
    this.mentionRuleService = new MentionRuleService(ruleServiceDeps);
    this.escalationService = new EscalationRuleService(ruleServiceDeps);

    // ThresholdService needs ruleEvaluator to call back for escalation checks
    this.thresholdService = new ThresholdRuleService({
      ...ruleServiceDeps,
      ruleEvaluator: this
    });

    // Score-aggregation rules are additive and domain-scoped — only domains
    // whose events carry numeric scores (e.g. academics) ever define them.
    // Discipline has none, so instantiating here is inert for it.
    this.scoreAggregationService = new ScoreAggregationRuleService({
      ...ruleServiceDeps,
      domainId: this.domainId
    });
  }

  /**
   * Process all rules for a mention
   * @param {Object} mention - The mention to process
   * @returns {Promise<Object>} Rule result including created consequences
   */
  async processRulesForMention(mention) {
    try {
      let consequences = [];
      // Set context for detailed error reporting
      processingGuard.setContext({
        mentionId: mention.id,
        studentId: mention.studentId,
        studentName: mention.studentName,
        offense: getOffenceField(mention),
        operation: 'processRulesForMention'
      });

      disciplineLogger.critical('🔥 PROCESSING RULES FOR MENTION', {
        mentionId: mention.id,
        student: mention.studentName,
        offense: getOffenceField(mention),
        schoolId: mention.schoolId
      }, 'mentions');

      // CRITICAL: Check if item is under adjudication - skip rules if so
      if (mention.adjudicationStatus === 'pending' || mention.skipRuleEvaluation) {
        disciplineLogger.info('⚖️ SKIPPING RULE EVALUATION - Item under adjudication', {
          mentionId: mention.id,
          adjudicationStatus: mention.adjudicationStatus,
          adjudicationId: mention.adjudicationId
        });
        return { consequences }; // Exit early - no rule processing for adjudicated items
      }

      // CRITICAL: Validate schoolId for hierarchical collections
      if (!mention.schoolId) {
        disciplineLogger.critical('❌ RULE PROCESSING ABORTED: mention.schoolId is missing', {
          mentionId: mention.id,
          studentId: mention.studentId,
          studentName: mention.studentName
        });
        throw new Error('mention.schoolId is required for rule processing with hierarchical collections');
      }

      if (mention.ruleConfiguration) {
        assertOriginalRuleConfiguration(mention.ruleConfiguration,
          await this.ruleService.getActiveRules(mention.schoolId),
          await this.ruleService.getPhasePolicy(mention.schoolId));
      }

      // Initialize ContractCache with schoolId for this processing cycle
      await contractCache.initialize(mention.schoolId);

      // FETCH ALL STUDENT DATA ONCE!!!
      const studentData = await studentDataFetcher.fetchStudentData(mention.studentId, mention.schoolId, this.domainId);

      disciplineLogger.info('📦 Student data fetched for rule evaluation', {
        studentId: mention.studentId,
        mentions: studentData.mentions.length,
        consequences: studentData.consequences.length,
        fetchTimeMs: studentData.stats.fetchTimeMs
      });

      // Get contract from pre-fetched data
      const contract = studentData.activeContract;
      if (contract) {
        disciplineLogger.info('Student has active contract');
      }

      // STEP 1: Process zero-tolerance rules FIRST (immediate consequences)
      consequences = await this.zeroToleranceService.processZeroToleranceRules(
        mention,
        studentData
      );

      // STEP 2: Process accountability rules (temporary periods of immediate consequences)
      const accountabilityTriggered = await this.accountabilityService.processAccountabilityRules(mention, studentData);

      // STEP 3: Process mention rules ONLY if accountability didn't trigger (mutual exclusivity)
      if (!accountabilityTriggered) {
        const mentionRuleConsequences = await this.mentionRuleService.processMentionRules(
          mention,
          contract,
          studentData
        );
        consequences.push(...mentionRuleConsequences);
      } else {
        disciplineLogger.info('Skipping mention rules (accountability triggered)');
      }

      // NOTE: Threshold rules are checked automatically in ConsequenceService.createDisciplineConsequence()
      // This happens when any consequence is created, so no separate step needed here

      // STEP 4: Score-aggregation rules (academics et al.). Gated off discipline
      // — these rules aggregate over numeric scores, which discipline never has.
      // They keep their own per-window ledger (a new score changes the aggregate,
      // so the per-event processedForRules flag can't model re-evaluation).
      if (this.domainId !== 'discipline') {
        await this.scoreAggregationService.processScoreAggregationRules(mention, studentData);
      }

      if (mention.ruleConfiguration) {
        assertOriginalRuleConfiguration(mention.ruleConfiguration,
          await this.ruleService.getActiveRules(mention.schoolId),
          await this.ruleService.getPhasePolicy(mention.schoolId));
      }

      // Mark mention as processed for rules (include schoolId for hierarchical path)
      await this.mentionService.updateMention(mention.id, {
        schoolId: mention.schoolId,
        processedForRules: true
      });

      // Clear context after successful processing
      processingGuard.clearContext();
      return { consequences };

    } catch (error) {

      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error processing rules for mention:', error);

      // Get processing guard metrics for error context
      const metrics = processingGuard.getMetrics();

      // Log detailed error information for debugging
      disciplineLogger.error('❌ RULE PROCESSING FAILED', {
        mentionId: mention.id,
        studentName: mention.studentName,
        error: error.message,
        errorCode: error.code,
        stack: error.stack,
        readMetrics: {
          sessionReads: metrics.sessionReads,
          topOperations: metrics.topOperations,
          sessionDuration: metrics.sessionDuration
        }
      }, 'mentions');

      // Rethrow the error so the UI knows processing failed
      // This prevents mentions from appearing successful when rules fail
      throw error;
    }
  }

  /**
   * Get active contract for a student
   * @param {string} studentId - Student ID
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Object|null>} Active contract or null
   */
  async getActiveContract(studentId, schoolId) {
    try {
      // CRITICAL: Validate schoolId for hierarchical collection
      if (!schoolId) {
        console.error('schoolId is required for hierarchical studentContracts collection');
        return null;
      }

      const contractQuery = query(
        collection(db, 'schools', schoolId, 'studentContracts'),
        where('studentId', '==', studentId),
        where('active', '==', true),
        where('startDate', '<=', Timestamp.now()),
        where('endDate', '>=', Timestamp.now())
      );

      const contracts = await getDocs(contractQuery);
      processingGuard.trackReads('RuleEvaluator.getContract', contracts.size, {
        studentId
      });
      if (!contracts.empty) {
        return { id: contracts.docs[0].id, ...contracts.docs[0].data() };
      }
      return null;
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error getting student contract:', error);
      return null; // Continue with normal processing if contract check fails
    }
  }

  /**
   * Process threshold rules after a consequence is created
   * This is called by ConsequenceService after creating any demerit or detention
   * @param {Object} consequence - The created consequence
   * @param {string} studentId - Student ID
   * @returns {Promise<void>}
   */
  async processThresholdRules(consequence, studentId) {
    disciplineLogger.info(`🔄 PROCESSING THRESHOLD RULES`, {
      consequenceId: consequence.id,
      consequenceType: consequence.type,
      studentId,
      studentName: consequence.studentName
    });

    // Initialize ContractCache with schoolId for this processing cycle
    if (consequence.schoolId) {
      await contractCache.initialize(consequence.schoolId);
    }

    // FETCH STUDENT DATA ONCE for threshold evaluation
    const studentData = await studentDataFetcher.fetchStudentData(studentId, consequence.schoolId, this.domainId);

    disciplineLogger.info('📦 Student data fetched for threshold evaluation', {
      studentId,
      consequences: studentData.consequences.length,
      fetchTimeMs: studentData.stats.fetchTimeMs
    });

    await this.thresholdService.checkThresholdsAfterCreation(consequence, studentId, studentData);
  }

  /**
   * Check if a consequence should be escalated BEFORE creation
   * This is called by ConsequenceService/ThresholdService before creating a consequence
   * @param {string} studentId - Student ID
   * @param {string} proposedConsequenceType - The consequence type about to be created
   * @param {Object} context - Additional context
   * @returns {Promise<Object|null>} Escalation result or null
   */
  async checkEscalationBeforeCreation(studentId, proposedConsequenceType, context = {}) {
    return await this.escalationService.checkEscalationBeforeCreation(
      studentId,
      proposedConsequenceType,
      context
    );
  }

  /**
   * Check if a consequence should be escalated AFTER creation (transaction-based)
   * This is called within a transaction after creating a consequence document
   * @param {Object} transaction - Firestore transaction
   * @param {string} studentId - Student ID
   * @param {string} consequenceType - The consequence type that was created
   * @param {string} consequenceId - The ID of the created consequence
   * @param {Object} context - Additional context
   * @returns {Promise<Object|null>} Escalation result or null
   */
  async checkEscalationAfterCreation(transaction, studentId, consequenceType, consequenceId, context = {}) {
    return await this.escalationService.checkEscalationAfterCreation(
      transaction,
      studentId,
      consequenceType,
      consequenceId,
      context
    );
  }

  /**
   * Process reset-after-use for escalation rules
   * This is called after an escalated consequence is successfully created
   * @param {string} ruleId - Escalation rule ID
   * @param {string} studentId - Student ID
   * @param {Array} consumedDocs - Consumed consequence documents
   * @returns {Promise<void>}
   */
  async processEscalationReset(ruleId, studentId, consumedDocs) {
    await this.escalationService.processResetAfterUse(ruleId, studentId, consumedDocs);
  }
}
